// Long-form guides. Author is the real operator; no invented people or statistics.
export const AUTHOR = {
    name: "RecipeFox AI Editorial",
    org: "FOXVISITS LTD, Scotland",
    bio: "The team that builds RecipeFox AI and runs its WordPress recipe publishing pipeline: recipe plugins, structured data, internal linking and quality checks.",
};

export const GUIDES = [
    {
        slug: "recipe-schema-checklist-wordpress",
        title: "Recipe Schema Checklist for WordPress Food Blogs",
        description: "The Recipe structured data fields Google actually uses, which ones your plugin fills, and the mistakes that make recipe rich results disappear.",
        published: "2026-10-20", updated: "2026-10-20", readMins: 8, tag: "Structured data",
        takeaways: [
            "Google requires only name and image, but recommends a full set of times, yield, ingredients and instructions.",
            "Let WP Recipe Maker or WP Delicious output the schema; do not add a second hand-written Recipe block.",
            "Times must be ISO 8601 durations and should add up: prep plus cook (plus rest) equals total.",
            "Validate with the Rich Results Test after every template or plugin change.",
        ],
        sections: [
            {
                id: "required", h2: "Required versus recommended fields",
                paras: ["Google's recipe documentation lists only two required properties: name and image. Everything else is recommended, but a recipe with just a name and a picture rarely earns a rich result or a place in recipe carousels. In practice you want the full set below."],
                table: {
                    head: ["Property", "Why it matters", "Common mistake"],
                    rows: [
                        ["name", "The dish name shown in results", "Stuffing keywords: \"Best Easy Quick Pasta Recipe\""],
                        ["image", "Thumbnail in results and carousels", "Small images; provide wide, 4:3 and 1:1 crops"],
                        ["prepTime, cookTime, totalTime", "Time shown in results and filters", "Total that does not equal the parts"],
                        ["recipeYield", "Servings in results", "Missing, or text only such as \"a family\""],
                        ["recipeIngredient", "Ingredient list", "Quantities buried in notes"],
                        ["recipeInstructions", "HowToStep list for guided cooking", "One giant paragraph as a single step"],
                        ["nutrition.calories", "Calories per serving", "Values for the whole dish labelled per serving"],
                        ["recipeCategory, recipeCuisine, keywords", "Context for matching searches", "Dozens of near-duplicate terms"],
                        ["aggregateRating, review", "Stars in results", "Faked or self-awarded ratings"],
                    ],
                },
            },
            {
                id: "one-source", h2: "One source of schema per recipe",
                paras: [
                    "Recipe plugins exist largely to output correct Recipe schema. Problems start when a theme, an SEO plugin and a hand-added script each output their own Recipe block. Search engines then see two recipes with different times or yields on the same page.",
                    "Keep the recipe plugin as the only source. Yoast SEO and Rank Math both integrate with WP Recipe Maker and merge the recipe into their page graph rather than duplicating it.",
                ],
            },
            {
                id: "durations", h2: "Getting times right",
                paras: ["Schema times use ISO 8601 durations: PT20M is twenty minutes, PT1H15M is an hour and fifteen. The plugin converts your numbers, so the real risk is the numbers themselves. A recipe with 20 minutes prep, 45 minutes cook and a 2 hour 5 minute total either forgot a resting or chilling step or made an error. Readers notice, and the inconsistency weakens trust in the whole card."],
                code: `"prepTime": "PT20M",
"cookTime": "PT45M",
"totalTime": "PT1H5M"`,
                codeLabel: "Consistent durations",
            },
            {
                id: "instructions", h2: "Instructions that work as steps",
                bullets: [
                    "One action per step, starting with a verb: \"Whisk the eggs with the sugar until pale.\"",
                    "Mention only ingredients that appear in the ingredient list, with the same names",
                    "Put doneness cues in the step (\"until the edges are golden, 10 to 12 minutes\")",
                    "Group steps only when the recipe has real components, such as a sauce and a topping",
                    "Do not split a simple recipe into 15 tiny steps; four to eight is typical for a weeknight dish",
                ],
            },
            {
                id: "images", h2: "Images",
                paras: ["Provide a high-resolution featured image and let your plugin generate the crops. Alt text should describe the actual dish (\"Bowl of Greek peas simmered in tomato sauce with dill\"), not repeat the keyword. Every image on the page needs its own description."],
            },
            {
                id: "validate", h2: "Validate, then monitor",
                steps: [
                    { t: "Rich Results Test", b: "Paste a recipe URL into Google's Rich Results Test and fix every error. Warnings for missing optional fields are worth fixing when you have the data." },
                    { t: "Search Console enhancements", b: "The Recipes report shows valid items and issues across the whole site. A sudden drop usually means a theme or plugin update changed the output." },
                    { t: "Spot-check the page", b: "Make sure the visible card matches the schema. Data that exists only in schema and not on the page is against Google's guidelines." },
                ],
                after: ["RecipeFox AI fills every recommended field the recipe plugin supports, then lets the plugin output the schema; see [WP Recipe Maker](/wp-recipe-maker) and [WP Delicious](/wp-delicious)."],
            },
        ],
        faq: [
            { q: "Do I need a recipe plugin for recipe schema?", a: "Not strictly, but it is the simplest reliable option on WordPress. WP Recipe Maker and WP Delicious output valid Recipe schema from the card fields." },
            { q: "Can I add ratings to my schema myself?", a: "Only ratings from real readers. Self-assigned ratings violate Google's review snippet guidelines and can lead to a manual action." },
            { q: "Does FAQ schema still show in Google?", a: "Google limits FAQ rich results to a small set of authoritative sites, but clear question-and-answer content is still useful to readers and to AI answer engines." },
        ],
        related: ["wp-recipe-maker-vs-wp-delicious", "ai-recipes-without-thin-content"],
    },
    {
        slug: "ai-recipes-without-thin-content",
        title: "How to Publish AI Recipes Without Creating Thin Content",
        description: "A practical editorial process for food bloggers using AI: what Google's scaled content policy means, what to add by hand, and the checks that keep recipes accurate.",
        published: "2026-10-20", updated: "2026-10-20", readMins: 9, tag: "Content quality",
        takeaways: [
            "Google judges helpfulness and intent, not the tool. Mass-producing near-identical pages is the risk.",
            "Cover topics your site can be trusted on; avoid publishing every keyword a tool suggests.",
            "Add what only you have: photos of your own cooking, notes from making the dish, honest substitutions.",
            "Never claim testing, ratings or reviews that did not happen.",
        ],
        sections: [
            {
                id: "policy", h2: "What Google's policy actually says",
                paras: [
                    "Google's spam policies describe \"scaled content abuse\": generating many pages primarily to manipulate rankings rather than to help people. The policy applies whether the pages come from AI, templates or low-paid writers. Separately, its guidance on AI content states that using automation is not against its guidelines when the result is helpful.",
                    "For a recipe site, the warning signs are easy to recognize: hundreds of posts in a few weeks, recipes for dishes the site has no connection to, and articles that read the same apart from the dish name.",
                ],
            },
            {
                id: "focus", h2: "Choose a lane and stay in it",
                paras: ["Topical focus is one of the strongest signals a small site can send. A blog about Greek home cooking that publishes 200 Greek recipes looks like an authority. The same blog publishing air fryer wings, keto desserts and Thai curries looks like a content farm. Pick cuisines, diets or techniques you can speak to and grow outward slowly."],
            },
            {
                id: "pace", h2: "Publish at a sustainable pace",
                paras: ["A new domain going from zero to 300 posts in a month is unusual, whatever the quality. Spreading publication over weeks gives you time to review each post and gives search engines time to evaluate the site. A schedule of one or two posts a day per site, with randomized hours, is a reasonable ceiling for most blogs."],
            },
            {
                id: "add", h2: "What to add by hand",
                bullets: [
                    "At least one real photo of the dish as you made it, even a simple phone shot, alongside generated images",
                    "A short note from your own kitchen: what you changed, what surprised you, what your family thought",
                    "Local or seasonal context: where you buy an ingredient, what to use when it is out of season",
                    "An author page with your name, background and why you cook this food",
                    "Corrections when readers point out problems, and dates that reflect real updates",
                ],
            },
            {
                id: "checks", h2: "Accuracy checks before publishing",
                paras: ["AI-written recipes fail in predictable ways. Check, or automate checks for:"],
                bullets: [
                    "Times that do not add up, or a cook time too short for the method",
                    "Ingredients listed but never used, or used but never listed",
                    "Oven temperatures that change between the intro and the steps",
                    "Storage advice that contradicts itself",
                    "Nutrition figures that are implausible for the ingredients",
                    "Claims of personal testing, awards or ratings",
                ],
                after: ["RecipeFox AI runs these checks automatically and repairs a recipe before publishing; see [how the quality checks work](/wp-recipe-maker#quality)."],
            },
            {
                id: "links", h2: "Link like an editor",
                paras: ["Internal links should help a reader take the next step: the sauce recipe used in this dish, a roundup of similar meals. Link only to pages that exist and fit. [Recipe roundups](/recipe-listicles) built from your own catalog are a natural way to connect older recipes."],
            },
        ],
        faq: [
            { q: "Will Google detect that my recipes were written with AI?", a: "Detection is not the point of Google's policies. Pages are evaluated on helpfulness, originality and intent. An accurate, well-edited recipe on a focused site is judged like any other." },
            { q: "How many AI recipes can I publish per day?", a: "There is no published limit. For a new site, a slow, steady pace with real editorial review is safer than a large burst." },
            { q: "Should I disclose AI use?", a: "Being transparent about how content is produced builds trust. Many publishers note that AI assisted with drafting while a person reviewed the recipe." },
        ],
        related: ["recipe-schema-checklist-wordpress", "internal-linking-food-blog-roundups"],
    },
    {
        slug: "wp-recipe-maker-vs-wp-delicious",
        title: "WP Recipe Maker vs WP Delicious: Which Recipe Plugin to Choose",
        description: "A practical comparison of the two most used WordPress recipe plugins: data model, recipe cards, schema, taxonomies, free versus paid features and automation support.",
        published: "2026-10-20", updated: "2026-10-20", readMins: 7, tag: "WordPress",
        takeaways: [
            "Both plugins output valid Recipe schema and have usable free versions.",
            "WP Recipe Maker stores recipes as separate items embedded in posts; WP Delicious makes each recipe its own post.",
            "Pick based on how you want archives and URLs to work, not on feature lists alone.",
            "Switching later is possible but takes migration work, so decide early.",
        ],
        sections: [
            {
                id: "model", h2: "The biggest difference: how recipes are stored",
                paras: [
                    "WP Recipe Maker keeps each recipe as a separate item that you embed into a normal blog post with a block. The post is the page that ranks; the recipe card lives inside it. You can reuse one recipe in several posts.",
                    "WP Delicious treats each recipe as its own post type with its own URL, archive pages and taxonomies such as course, cuisine, cooking method and recipe keys. It suits sites that want a recipe database feel with browsable filters.",
                ],
            },
            {
                id: "compare", h2: "Comparison",
                table: {
                    head: ["", "WP Recipe Maker", "WP Delicious"],
                    rows: [
                        ["Recipe storage", "Recipe item embedded in a post", "Recipe is its own post type"],
                        ["Schema output", "Yes, integrates with Yoast and Rank Math graphs", "Yes"],
                        ["Taxonomies", "Course, cuisine, keywords plus custom", "Course, cuisine, method, recipe keys, tags"],
                        ["Free version", "Yes, full card and schema", "Yes"],
                        ["Paid extras", "Nutrition labels, ratings, collections", "Advanced features in Pro"],
                        ["Best for", "Classic food blogs, story-led posts", "Recipe-database style sites"],
                    ],
                },
            },
            {
                id: "automation", h2: "Automation and AI publishing",
                paras: ["Neither plugin accepts its full recipe data through the standard WordPress REST API, which is why most AI tools leave the card empty. RecipeFox AI uses a small Bridge plugin to write native data for either one: see [WP Recipe Maker publishing](/wp-recipe-maker) and [WP Delicious publishing](/wp-delicious)."],
            },
            {
                id: "decide", h2: "How to decide",
                bullets: [
                    "If your posts are stories with a recipe at the end, choose WP Recipe Maker",
                    "If visitors browse by diet, course or method, consider WP Delicious",
                    "If you already have hundreds of recipes in one plugin, stay with it",
                ],
            },
        ],
        faq: [
            { q: "Can I use both plugins on one site?", a: "It is possible but rarely sensible, because recipes, schema and archives would be split. Use one per site." },
            { q: "Which is better for SEO?", a: "Neither has an inherent ranking advantage. Complete, accurate fields and fast pages matter far more than the plugin choice." },
        ],
        related: ["recipe-schema-checklist-wordpress", "internal-linking-food-blog-roundups"],
    },
    {
        slug: "internal-linking-food-blog-roundups",
        title: "Internal Linking for Food Blogs: Using Recipe Roundups",
        description: "How recipe roundups spread internal links to older recipes, how to choose topics your catalog can support, and the rules that keep links useful.",
        published: "2026-10-20", updated: "2026-10-20", readMins: 6, tag: "Internal linking",
        takeaways: [
            "Roundups target broad searches and push links to recipes that sit deep in your archive.",
            "Only build a roundup when you have enough genuinely matching recipes.",
            "Link with descriptive anchors, to live pages on your own site, once per recipe.",
        ],
        sections: [
            {
                id: "why", h2: "Why recipe sites struggle with internal links",
                paras: ["A typical food blog publishes in date order. After a year, most recipes are reachable only through pagination or category archives, several clicks from the home page. Search engines crawl and value those pages less. Roundups fix this by creating curated hubs that link straight to them."],
            },
            {
                id: "topics", h2: "Choosing roundup topics",
                bullets: [
                    "Start from what you have: group published recipes by ingredient, season, occasion or method",
                    "Prefer searches people make before they know the dish (\"what to make with leftover rice\")",
                    "Require a real match: a dessert does not belong in a dinner roundup",
                    "Do not repeat a topic you already covered; update the existing roundup instead",
                ],
            },
            {
                id: "rules", h2: "Linking rules that keep roundups useful",
                steps: [
                    { t: "Live pages only", b: "Every link should point to a published recipe on the same site. Recheck before publishing; drafts and deleted posts cause broken links." },
                    { t: "Descriptive anchors", b: "\"Get the Greek pea stew recipe\" tells readers and search engines what is behind the link; \"click here\" does not." },
                    { t: "One link per recipe", b: "Repeating the same link in a short article adds nothing." },
                    { t: "No forced links", b: "If no recipe fits a position, write it as an idea without a link." },
                ],
            },
            {
                id: "structure", h2: "Structure that answer engines can cite",
                paras: ["Open with a direct answer, number the positions, give each dish one clear reason to pick it, and finish with a few common questions. ItemList structured data mirrors the visible list. [RecipeFox AI listicles](/recipe-listicles) follow this structure and build topics from your own published recipes."],
            },
        ],
        faq: [
            { q: "How many recipes should a roundup include?", a: "As many as genuinely fit. Ten to fifteen is common, but eight strong picks beat twenty weak ones." },
            { q: "Should roundups link to other websites?", a: "They can, but a roundup on your own site is most valuable when it connects your own recipes." },
        ],
        related: ["ai-recipes-without-thin-content", "recipe-schema-checklist-wordpress"],
    },
    {
        slug: "wordpress-application-passwords-food-blog",
        title: "WordPress Application Passwords for Recipe Publishing Tools",
        description: "What Application Passwords are, how to create and revoke one, and how to fix 401 and 403 errors when a publishing tool cannot reach your WordPress site.",
        published: "2026-10-20", updated: "2026-10-20", readMins: 5, tag: "WordPress",
        takeaways: [
            "Application Passwords are built into WordPress and can be revoked without changing your login password.",
            "A 401 error usually means a wrong username or password; a 403 usually means a firewall or security plugin.",
            "Allow-list the tool's server address in Cloudflare or your host's firewall when 403 errors appear.",
        ],
        sections: [
            {
                id: "what", h2: "What an Application Password is",
                paras: ["Since WordPress 5.6, every user profile can create Application Passwords: long random passwords that work only for the REST API, not for logging into wp-admin. Each one has a name, so you can see which tool uses it and revoke it individually."],
            },
            {
                id: "create", h2: "Creating one",
                steps: [
                    { t: "Open your profile", b: "WP Admin, Users, Profile, then scroll to Application Passwords." },
                    { t: "Name it", b: "Use the tool's name, for example RecipeFox AI, and click Add New Application Password." },
                    { t: "Copy it once", b: "WordPress shows the password only once. Paste it into the tool together with your WordPress username." },
                ],
            },
            {
                id: "errors", h2: "Fixing connection errors",
                table: {
                    head: ["Error", "Usual cause", "Fix"],
                    rows: [
                        ["401 Unauthorized", "Wrong username or password, or the password was revoked", "Create a new Application Password and update it in the tool"],
                        ["403 Forbidden from WordPress", "User role lacks permission, or a security plugin disabled the REST API", "Use an Editor or Administrator account; allow REST API access for authenticated users"],
                        ["403 from Cloudflare or the host", "Firewall blocks the tool's server address", "Allow-list the address or the /wp-json/ path in Cloudflare WAF or the hosting firewall"],
                        ["Read timed out", "Slow hosting under load", "Publish fewer posts at once; RecipeFox queues retries one at a time per site"],
                    ],
                },
            },
            {
                id: "security", h2: "Good security habits",
                bullets: [
                    "Create a separate Application Password for each tool",
                    "Revoke passwords for tools you stop using",
                    "Use an Editor role account for publishing when you do not need plugin management",
                ],
            },
        ],
        faq: [
            { q: "My Application Passwords section is missing. Why?", a: "A security plugin or your host may have disabled it, or the site is not served over HTTPS. Check the security plugin settings first." },
            { q: "Does revoking an Application Password log me out?", a: "No. It only stops the tool that used that password." },
        ],
        related: ["wp-recipe-maker-vs-wp-delicious", "recipe-schema-checklist-wordpress"],
    },
];

export const guideBySlug = (slug) => GUIDES.find(g => g.slug === slug);
